import {
  Box,
  BoxProps,
  Flex,
  FlexAnchor,
  Icon,
  IconProps,
  mdBumps,
  smBumps,
  Typo,
  WriskColors,
} from '@wrisk/ui-components'
import { size } from 'lodash-es'
import React, { FunctionComponent, useCallback } from 'react'

import {
  getConditionalCoverChange,
  getCoverOptionChange,
  getEndorsementDifference,
  isProductEndorsement,
  PolicyVersion,
} from '../../../domain'
import { useConditionalCovers, useOptionalCovers } from '../../../hooks/covers'
import {
  tCovers,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { usePolicy } from '../../product/policy/PolicyProvider'
import { useProduct } from '../../product/productContext'
import { Endorsements } from '../endorsements'
import { useConditionalCoverModal } from './useConditionalCoverModal'
import { useOptionalCoverModal } from './useOptionalCoverModal'

export interface ProposalCoverSectionProps extends BoxProps {
  updatedPolicyVersion: PolicyVersion
}

const tKey = TKey('components.cover-changes')

export const CoverChanges: FunctionComponent<ProposalCoverSectionProps> = ({
  updatedPolicyVersion: { quote: updatedQuote },
  ...props
}) => {
  const { t } = useWriskTranslation()

  const {
    policy: {
      currentPolicyVersion: { quote: currentQuote },
    },
  } = usePolicy()
  const { product } = useProduct()

  const endorsements = getEndorsementDifference(
    updatedQuote.endorsements.filter(isProductEndorsement(product)),
    currentQuote.endorsements.filter(isProductEndorsement(product)),
  )

  const currentConditionalCovers = useConditionalCovers(currentQuote, product)
  const updatedConditionalCovers = useConditionalCovers(updatedQuote, product)
  const conditionalCoverChanges = getConditionalCoverChange(
    currentConditionalCovers,
    updatedConditionalCovers,
  )

  const currentOptionalCovers = useOptionalCovers(currentQuote, product)
  const updatedOptionalCovers = useOptionalCovers(updatedQuote, product)
  const optionalCoverChanges = getCoverOptionChange(
    currentOptionalCovers,
    updatedOptionalCovers,
  )

  const showOptionalCoverModal = useOptionalCoverModal(updatedQuote)
  const showConditionalCoverModal = useConditionalCoverModal()

  const handleOptionalCover = useCallback((item: string) => {
    const cover = updatedOptionalCovers.find(({ coverGroup }) => coverGroup === item)

    if (cover) {
      showOptionalCoverModal(cover)
    }
  }, [])

  const handleConditionalCover = useCallback((item: string) => {
    const cover = updatedConditionalCovers.find(({ coverCode }) => coverCode === item)

    if (cover) {
      showConditionalCoverModal(cover)
    }
  }, [])

  return [endorsements, conditionalCoverChanges, optionalCoverChanges].some(size) ? (
    <Box variant='raised' {...props}>
      <Box p={mdBumps}>
        <Typo fontWeight='bold'>{t(tKey('header'))}</Typo>
      </Box>
      <CoverChange
        header={t(tKey('headers', 'conditional-covers-added'))}
        icon='tick'
        onClick={handleConditionalCover}
        items={conditionalCoverChanges
          .filter(({ type }) => type === 'ADDED')
          .map(({ cover }) => cover.coverCode)}
        borderTopWidth={1}
      />
      <CoverChange
        header={t(tKey('headers', 'optional-covers-added'))}
        icon='tick'
        onClick={handleOptionalCover}
        items={optionalCoverChanges
          .filter(({ type }) => type === 'ADDED')
          .map(({ cover }) => cover.coverGroup)}
        borderTopWidth={1}
      />
      <CoverChange
        header={t(tKey('headers', 'optional-covers-available'))}
        icon='plus'
        onClick={handleOptionalCover}
        items={optionalCoverChanges
          .filter(({ type }) => type === 'AVAILABLE')
          .map(({ cover }) => cover.coverGroup)}
        borderTopWidth={1}
      />
      <CoverChange
        header={t(tKey('headers', 'conditional-covers-removed'))}
        icon='cross'
        onClick={handleConditionalCover}
        items={conditionalCoverChanges
          .filter(({ type }) => type === 'REMOVED')
          .map(({ cover }) => cover.coverCode)}
        borderTopWidth={1}
      />
      <CoverChange
        header={t(tKey('headers', 'optional-covers-removed'))}
        icon='cross'
        onClick={handleOptionalCover}
        items={optionalCoverChanges
          .filter(({ type }) => type === 'REMOVED')
          .map(({ cover }) => cover.coverGroup)}
        borderTopWidth={1}
      />
      <Endorsements
        p={mdBumps}
        quote={updatedQuote}
        endorsements={endorsements}
        borderTopWidth={1}
      />
    </Box>
  ) : null
}

const CoverChange: FunctionComponent<
  {
    header: string
    items: string[]
    icon: IconProps['icon']
    color?: WriskColors
    onClick?: (item: string) => void
  } & BoxProps
> = ({ header, items, icon, color = 'body', onClick, ...props }) => {
  const { t } = useWriskTranslation()

  return items.length ? (
    <Box p={2} {...props}>
      <Typo py={smBumps} px={smBumps} typoSize='sm' color='bodySecondary'>
        {header}
      </Typo>
      {items.map((item) => (
        <FlexAnchor
          key={item}
          px={smBumps}
          py={3}
          variant='highlight'
          columnGap={smBumps}
          onClick={() => onClick?.(item)}
        >
          <Flex columnGap={smBumps}>
            <Icon icon={icon} color={color} />
            <Typo color={color}>{t(tCovers(item, 'name'))}</Typo>
          </Flex>
          <Icon icon='info' size='small' />
        </FlexAnchor>
      ))}
    </Box>
  ) : null
}
